import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBluetoothStore, PrintSize } from '../../store/bluetooth.store';
import { bluetoothService, DeviceType, BtDeviceInfo } from '../../services/bluetooth.service';
import { printTest } from '../../services/label-print.service';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const PRINT_SIZES: { value: PrintSize; label: string; icon: string }[] = [
    { value: '76x130', label: '76×130', icon: 'crop_portrait' },
    { value: '100x150', label: '100×150', icon: 'crop_landscape' },
    { value: '100x170', label: '100×170', icon: 'crop_portrait' },
    { value: '100x100', label: '100×100', icon: 'crop_portrait' },
    { value: '100x130', label: '100×130', icon: 'crop_portrait' },
    { value: '50x30', label: '50×30', icon: 'label' },
    { value: '40x30', label: '40×30', icon: 'label' },
];

const PRINT_PROTOCOLS: { value: 'auto' | 'tspl' | 'cpcl' | 'escpos'; label: string; desc: string }[] = []; // Moved inside component to use t()

// ─── Unified BLE Connect Modal ───────────────────────────────────────
type BlePhase = 'start' | 'scanning' | 'results' | 'connecting' | 'error';

interface BleModalState {
    show: boolean;
    type: DeviceType;
    phase: BlePhase;
    devices: BtDeviceInfo[];
    connectingId: string | null;
    error: string | null;
    scanFilter: 'smart' | 'all';
}

const INIT_BLE_MODAL: BleModalState = { show: false, type: 'scale', phase: 'start', devices: [], connectingId: null, error: null, scanFilter: 'smart' };

const BluetoothConnection: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const {
        scaleDevice, scaleConnecting, lastWeight,
        printerDevice, printerConnecting, printSize, printProtocol,
        isScanning, scanTarget,
        connectScale, connectScaleAll, disconnectScale,
        connectPrinter, connectPrinterAll, disconnectPrinter,
        setPrintSize, setPrintProtocol, resetAll,
    } = useBluetoothStore();

    const [weightPulse, setWeightPulse] = useState(false);
    const [testPrinting, setTestPrinting] = useState(false);
    const [bleModal, setBleModal] = useState<BleModalState>(INIT_BLE_MODAL);
    const pickerResolveRef = useRef<((d: BtDeviceInfo | null) => void) | null>(null);

    const btSupported = bluetoothService.isSupported();
    const isAndroidBridge = bluetoothService.isAndroidBridge();

    const protocols: { value: 'auto' | 'tspl' | 'cpcl' | 'escpos'; label: string; desc: string }[] = [
        { value: 'auto', label: t('bluetooth.protocol_auto', '自动识别'), desc: t('bluetooth.protocol_auto_desc', '智能匹配打印机指令集 (推荐)') },
        { value: 'tspl', label: 'TSPL', desc: t('bluetooth.protocol_tspl_desc', '通用标签指令 (Xprinter/TSC/Gprinter)') },
        { value: 'cpcl', label: 'CPCL', desc: t('bluetooth.protocol_cpcl_desc', '汉印/汉印D35/标龙/斑马') },
        { value: 'escpos', label: 'ESC/POS', desc: t('bluetooth.protocol_escpos_desc', '普通小票打印指令') },
    ];

    // Register the native Android device picker + real-time scan progress streaming
    useEffect(() => {
        // Picker: called by service AFTER scan completes with final list (auto-pick if single device)
        bluetoothService.setDevicePicker((devices) => {
            return new Promise<BtDeviceInfo | null>((resolve) => {
                pickerResolveRef.current = resolve;
                // Sort final list by signal strength (strongest first)
                const sorted = [...devices].sort((a, b) => (b.rssi ?? -100) - (a.rssi ?? -100));
                setBleModal(prev => ({ ...prev, phase: 'results', devices: sorted }));
            });
        });

        // Scan progress: fires for EACH device as it's discovered — real-time streaming
        bluetoothService.setScanProgressCallback((device) => {
            setBleModal(prev => {
                // Only update during scanning or results phase
                if (prev.phase !== 'scanning' && prev.phase !== 'results') return prev;
                const exists = prev.devices.some(d => d.id === device.id);
                const newDevices = exists
                    ? prev.devices.map(d => d.id === device.id ? device : d)
                    : [...prev.devices, device];
                // Sort by RSSI descending (strongest signal = most likely target device, shown first)
                newDevices.sort((a, b) => (b.rssi ?? -100) - (a.rssi ?? -100));
                return {
                    ...prev,
                    phase: 'results',        // Switch to results on first device found
                    devices: newDevices,
                };
            });
        });

        return () => {
            // Cleanup on unmount
            bluetoothService.setScanProgressCallback(null);
        };
    }, []);

    // Weight pulse animation
    useEffect(() => {
        if (lastWeight) {
            setWeightPulse(true);
            const t = setTimeout(() => setWeightPulse(false), 300);
            return () => clearTimeout(t);
        }
    }, [lastWeight?.weight]);

    // ─── Modal helpers ──────────────────────────────────────────────
    const closeBleModal = () => {
        // Cancel any pending picker promise
        pickerResolveRef.current?.(null);
        pickerResolveRef.current = null;
        setBleModal(INIT_BLE_MODAL);
    };

    /** Open the modal — always show mode-selection panel first */
    const openBleModal = (type: DeviceType) => {
        // Always show the 'start' panel so user can choose smart search or search-all
        setBleModal({ ...INIT_BLE_MODAL, show: true, type, phase: 'start' });
    };

    /** Start scanning — works for both Android Bridge and Web Bluetooth */
    const handleStartScan = async (acceptAll: boolean) => {
        const { type } = bleModal;
        setBleModal(prev => ({ ...prev, phase: 'scanning', scanFilter: acceptAll ? 'all' : 'smart' }));
        try {
            if (acceptAll) {
                type === 'scale' ? await connectScaleAll() : await connectPrinterAll();
            } else {
                type === 'scale' ? await connectScale() : await connectPrinter();
            }
            toast.success(type === 'scale' ? t('bluetooth.scale_connected', '电子秤已连接 ✓') : t('bluetooth.printer_connected', '打印机已连接 ✓'));
            setBleModal(INIT_BLE_MODAL);
        } catch (err: any) {
            if (err.message === '未选择设备') {
                // User dismissed picker — go back to start
                setBleModal(prev => ({ ...prev, phase: 'start' }));
            } else {
                setBleModal(prev => ({ ...prev, phase: 'error', error: err.message || t('bluetooth.connection_failed', '连接失败') }));
            }
        }
    };

    /** Android: user picked a device from the results list */
    const handlePickDevice = (device: BtDeviceInfo) => {
        setBleModal(prev => ({ ...prev, phase: 'connecting', connectingId: device.id }));
        pickerResolveRef.current?.(device);
        pickerResolveRef.current = null;
    };

    /** Results header: switch to search-all scan */
    const handleScanAll = () => {
        // Cancel current picker promise and go back to start
        pickerResolveRef.current?.(null);
        pickerResolveRef.current = null;
        // Directly launch search-all without showing start panel again
        const { type } = bleModal;
        setBleModal(prev => ({ ...prev, phase: 'scanning', scanFilter: 'all' }));
        const connectFn = type === 'scale' ? connectScaleAll : connectPrinterAll;
        connectFn()
            .then(() => {
                toast.success(type === 'scale' ? '电子秤已连接 ✓' : '打印机已连接 ✓');
                setBleModal(INIT_BLE_MODAL);
            })
            .catch((err: any) => {
                if (err.message === '未选择设备') {
                    setBleModal(prev => ({ ...prev, phase: 'start' }));
                } else {
                    setBleModal(prev => ({ ...prev, phase: 'error', error: err.message || '连接失败' }));
                }
            });
    };

    const handleDisconnect = (type: DeviceType) => {
        if (type === 'scale') {
            disconnectScale();
            toast(t('bluetooth.scale_disconnected', '电子秤已断开'), { icon: '⚡' });
        } else {
            disconnectPrinter();
            toast(t('bluetooth.printer_disconnected', '打印机已断开'), { icon: '⚡' });
        }
    };

    // ─── Unified BLE Connect Modal ───────────────────────────────────────
    const typeIcon = bleModal.type === 'scale' ? 'monitor_weight' : 'print';
    const typeName = bleModal.type === 'scale' ? t('bluetooth.scale', '电子秤') : t('bluetooth.printer', '打印机');

    const BleConnectModal = () => (
        <div className="fixed inset-0 z-[80] flex items-end justify-center" onClick={closeBleModal}>
            <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" />
            <div
                className="relative w-full max-w-lg bg-[#0a1628] rounded-t-3xl border-t border-white/10 animate-slide-up overflow-hidden flex flex-col"
                style={{ maxHeight: '85vh' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Drag handle */}
                <div className="flex justify-center pt-3 pb-0">
                    <div className="w-10 h-1 rounded-full bg-white/20" />
                </div>

                {/* ── Phase: scanning (animation) ─── */}
                {(bleModal.phase === 'scanning') && (
                    <div className="flex flex-col items-center px-6 pt-8 pb-10">
                        {/* Radar animation */}
                        <div className="relative flex items-center justify-center w-28 h-28 mb-6">
                            <div className="absolute w-28 h-28 rounded-full border border-primary/20 animate-ping" style={{ animationDuration: '2.5s' }} />
                            <div className="absolute w-20 h-20 rounded-full border border-primary/30 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.4s' }} />
                            <div className="absolute w-12 h-12 rounded-full border border-primary/50 animate-ping" style={{ animationDuration: '1.5s', animationDelay: '0.8s' }} />
                            <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/40 flex items-center justify-center z-10">
                                <span className="material-icons text-primary text-3xl">{typeIcon}</span>
                            </div>
                        </div>
                        <div className="text-white font-bold text-lg mb-1">{t('bluetooth.scanning_device', '正在扫描{{type}}设备', { type: typeName })}</div>
                        <div className="text-gray-500 text-sm mb-6">{t('bluetooth.scan_hint', '请确保设备已开启并处于蓝牙广播模式')}</div>
                        {/* Progress bar */}
                        <div className="w-full max-w-xs h-1 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-primary to-blue-400 rounded-full animate-pulse" style={{ width: '100%', animationDuration: '1.2s' }} />
                        </div>
                        <div className="text-gray-600 text-xs mt-2">{t('bluetooth.scan_wait', '扫描范围最多 12 秒，请稍候...')}</div>
                        <button onClick={closeBleModal} className="mt-6 px-6 py-2 rounded-2xl bg-white/5 text-gray-500 text-sm border border-white/5 hover:bg-white/10 active:scale-95 transition-all">
                            {t('common.cancel', '取消')}
                        </button>
                    </div>
                )}

                {/* ── Phase: results (device list) ─── */}
                {bleModal.phase === 'results' && (
                    <div className="flex flex-col min-h-0 flex-1">
                        {/* Header row — always visible */}
                        <div className="px-5 pt-4 pb-3 flex items-center justify-between flex-shrink-0 border-b border-white/5">
                            <div>
                                <div className="text-white font-bold text-base">{t('bluetooth.select_device', '选择蓝牙设备')}</div>
                                <div className="text-gray-500 text-xs mt-0.5">{t('bluetooth.discovery_count', '发现 {{count}} 个设备 · 点击即可连接', { count: bleModal.devices.length })}</div>
                            </div>
                            <button
                                onClick={handleScanAll}
                                className="flex-shrink-0 flex items-center gap-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 text-xs hover:bg-white/10 active:scale-95 transition-all"
                            >
                                <span className="material-icons text-sm">sensors</span>
                                {t('bluetooth.search_all', '搜索全部')}
                            </button>
                        </div>

                        {/* Device list — scrollable */}
                        <div className="overflow-y-auto flex-1 no-scrollbar">
                            {bleModal.devices.length === 0 ? (
                                <div className="flex flex-col items-center py-10 text-gray-600">
                                    <span className="material-icons text-4xl mb-2">bluetooth_disabled</span>
                                    <div className="text-sm">{t('bluetooth.no_device_found', '未找到匹配设备')}</div>
                                    <button onClick={handleScanAll} className="mt-4 px-4 py-2 rounded-xl bg-primary/10 text-primary text-sm border border-primary/20">{t('bluetooth.search_all_devices', '搜索全部设备')}</button>
                                </div>
                            ) : (
                                bleModal.devices.map((device) => (
                                    <button
                                        key={device.id}
                                        onClick={() => handlePickDevice(device)}
                                        className="w-full flex items-center gap-4 px-5 py-4 border-b border-white/5 hover:bg-white/5 active:bg-primary/10 transition-all"
                                    >
                                        <div className="relative flex-shrink-0">
                                            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary/20 to-blue-500/10 border border-primary/20 flex items-center justify-center">
                                                <span className="material-icons text-primary text-xl">
                                                    {device.type === 'printer' ? 'print' : device.type === 'scale' ? 'monitor_weight' : 'bluetooth'}
                                                </span>
                                            </div>
                                            {device.rssi && (
                                                <div className="absolute -bottom-1 -right-1 bg-[#0a1628] rounded-full px-0.5">
                                                    <span className="material-icons text-[10px]" style={{ color: device.rssi > -60 ? '#22c55e' : device.rssi > -75 ? '#f59e0b' : '#ef4444' }}>fiber_manual_record</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0 text-left">
                                            <div className="text-white font-semibold text-sm truncate">
                                                {device.name}
                                                {(device.type === 'scale' || device.type === 'printer') && (
                                                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-md bg-primary/15 text-primary/80 font-normal">
                                                        {device.type === 'scale' ? t('bluetooth.scale_tag', '秤') : t('bluetooth.printer_tag', '印')}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-gray-600 text-xs mt-0.5 font-mono">{device.id}</div>
                                        </div>
                                        {device.rssi && <div className="text-[10px] text-gray-600 text-right flex-shrink-0">{device.rssi}<br /><span className="text-gray-700">dBm</span></div>}
                                        <span className="material-icons text-gray-600 flex-shrink-0">chevron_right</span>
                                    </button>
                                ))
                            )}
                        </div>

                        {/* Cancel — always at bottom */}
                        <div className="px-5 py-4 flex-shrink-0 border-t border-white/5">
                            <button onClick={closeBleModal} className="w-full py-3 rounded-2xl bg-white/5 text-gray-500 text-sm font-bold border border-white/5 hover:bg-white/10 active:scale-95 transition-all">{t('common.cancel', '取消')}</button>
                        </div>
                    </div>
                )}

                {/* ── Phase: connecting ─── */}
                {bleModal.phase === 'connecting' && (
                    <div className="flex flex-col items-center px-6 pt-10 pb-12">
                        <div className="relative w-20 h-20 mb-6">
                            <div className="w-20 h-20 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="material-icons text-primary text-2xl">{typeIcon}</span>
                            </div>
                        </div>
                        <div className="text-white font-bold text-lg mb-1">{t('bluetooth.connecting', '正在连接中...')}</div>
                        <div className="text-gray-500 text-sm">{t('bluetooth.establishing_connection', '请稍候，正在建立{{type}}连接', { type: typeName })}</div>
                    </div>
                )}

                {/* ── Phase: error ─── */}
                {bleModal.phase === 'error' && (
                    <div className="flex flex-col items-center px-6 pt-8 pb-10">
                        <div className="w-20 h-20 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-5">
                            <span className="material-icons text-red-400 text-3xl">bluetooth_disabled</span>
                        </div>
                        <div className="text-red-400 font-bold text-base mb-2">{t('bluetooth.connection_error', '连接失败')}</div>
                        <div className="text-gray-500 text-sm text-center mb-6">{bleModal.error}</div>
                        <div className="flex gap-3 w-full">
                            <button
                                onClick={() => openBleModal(bleModal.type)}
                                className="flex-1 py-3 rounded-2xl bg-primary/10 text-primary font-bold text-sm border border-primary/20 hover:bg-primary/20 active:scale-95 transition-all"
                            >{t('bluetooth.rescan', '重新扫描')}</button>
                            <button
                                onClick={closeBleModal}
                                className="flex-1 py-3 rounded-2xl bg-white/5 text-gray-500 font-bold text-sm border border-white/5 hover:bg-white/10 active:scale-95 transition-all"
                            >{t('common.cancel', '取消')}</button>
                        </div>
                    </div>
                )}

                {bleModal.phase === 'start' && (
                    <>
                        <div className="px-6 pt-6 pb-4 text-center">
                            <div className="relative inline-flex items-center justify-center mb-4">
                                <div className="absolute w-24 h-24 rounded-full bg-primary/5 animate-ping" style={{ animationDuration: '2s' }} />
                                <div className="absolute w-16 h-16 rounded-full bg-primary/10 animate-ping" style={{ animationDuration: '1.5s', animationDelay: '0.3s' }} />
                                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center border border-primary/30 z-10">
                                    <span className="material-icons text-primary text-3xl">{typeIcon}</span>
                                </div>
                            </div>
                            <h2 className="text-xl font-bold text-white mb-1">{t('bluetooth.connect_type', '连接{{type}}', { type: typeName })}</h2>
                            <p className="text-gray-500 text-sm">{t('bluetooth.choose_method', '请选择搜索方式')}</p>
                        </div>
                        <div className="px-5 pb-4 space-y-2.5">
                            <button
                                onClick={() => handleStartScan(false)}
                                disabled={scaleConnecting || printerConnecting}
                                className="w-full flex items-center gap-4 p-4 bg-primary/10 hover:bg-primary/20 border border-primary/25 rounded-2xl transition-all active:scale-[0.98] disabled:opacity-50 group"
                            >
                                <div className="p-2.5 rounded-xl bg-primary/20 text-primary border border-primary/30 group-hover:shadow-primary/20 group-hover:shadow-md transition-all">
                                    <span className="material-icons text-2xl">bluetooth_searching</span>
                                </div>
                                <div className="flex-1 text-left">
                                    <div className="text-white font-bold">{t('bluetooth.smart_search', '智能搜索')}
                                        <span className="ml-2 text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded-md">{t('bluetooth.recommended', '推荐')}</span>
                                    </div>
                                    <div className="text-gray-500 text-xs">{t('bluetooth.smart_search_desc', '自动筛选{{type}}相关设备', { type: typeName })}</div>
                                </div>
                                <span className="material-icons text-primary/40">chevron_right</span>
                            </button>
                            <button
                                onClick={() => handleStartScan(true)}
                                disabled={scaleConnecting || printerConnecting}
                                className="w-full flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 border border-white/8 rounded-2xl transition-all active:scale-[0.98] disabled:opacity-50 group"
                            >
                                <div className="p-2.5 rounded-xl bg-white/10 text-gray-400 border border-white/10 group-hover:text-white transition-all">
                                    <span className="material-icons text-2xl">sensors</span>
                                </div>
                                <div className="flex-1 text-left">
                                    <div className="text-gray-300 font-bold">{t('bluetooth.search_all_devices_title', '搜索全部设备')}</div>
                                    <div className="text-gray-600 text-xs">{t('bluetooth.search_all_devices_desc', '显示所有蓝牙设备（含未识别设备）')}</div>
                                </div>
                                <span className="material-icons text-gray-600">chevron_right</span>
                            </button>
                        </div>
                        {!isAndroidBridge && (
                            <div className="mx-5 mb-4 p-3 rounded-xl bg-amber-500/5 border border-amber-500/10 flex items-start gap-2">
                                <span className="material-icons text-amber-500 text-sm mt-0.5">tips_and_updates</span>
                                <div className="text-[11px] text-amber-500/80 leading-relaxed">
                                    <strong>{t('bluetooth.hint', '提示')}：</strong>{t('bluetooth.pair_hint', '点击后浏览器将弹出蓝牙选择窗口，请在列表中选择要连接的设备后点击配对。')}
                                </div>
                            </div>
                        )}
                        <div className="px-5 pb-8">
                            <button onClick={closeBleModal} className="w-full py-3.5 rounded-2xl bg-white/5 text-gray-500 text-sm font-bold uppercase tracking-widest border border-white/5 hover:bg-white/10 active:scale-95 transition-all">{t('common.cancel', '取消')}</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-full bg-background-light dark:bg-background-dark">
            {/* Premium Header */}
            <header className="bg-slate-950/20 backdrop-blur-2xl z-50 sticky top-0 flex-shrink-0">
                <div className="px-6 py-4 flex items-center justify-between relative">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 flex items-center justify-center rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-white hover:bg-white/10 active:scale-95 transition-all"
                    >
                        <span className="material-icons text-xl">arrow_back_ios_new</span>
                    </button>

                    <div className="flex flex-col items-center">
                        <h1 className="text-sm font-black text-white tracking-[0.2em] uppercase">{t('settings.bluetooth_management', '蓝牙设备管理')}</h1>
                        <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="text-[9px] text-emerald-500/70 font-bold uppercase tracking-widest">{t('bluetooth.active_system', 'Active System')}</span>
                        </div>
                    </div>

                    <button
                        onClick={() => {
                            resetAll();
                            toast.success(t('bluetooth.hardware_reset_success', 'Connections reset'));
                        }}
                        className="w-10 h-10 flex items-center justify-center rounded-2xl bg-white/5 border border-white/5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 active:scale-95 transition-all group"
                        title="Reset Bluetooth"
                    >
                        <span className="material-icons text-xl transition-transform group-active:rotate-180 duration-500">restart_alt</span>
                    </button>
                </div>
                <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-white/5 to-transparent" />
            </header>

            <main className="flex-1 p-4 overflow-y-auto flex flex-col gap-6 pb-32 no-scrollbar">
                {/* BT Support Warning */}
                {!btSupported && (() => {
                    const reason = bluetoothService.getUnsupportedReason();
                    const ua = navigator.userAgent || '';
                    const isAndroidWebView = /Android/i.test(ua) && /wv|WebView/i.test(ua);
                    const currentUrl = window.location.href;
                    const chromeUrl = `intent://${currentUrl.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`;
                    return (
                        <div className="rounded-2xl bg-red-500/10 border border-red-500/20 overflow-hidden">
                            <div className="p-4 flex items-start gap-3">
                                <span className="material-icons text-red-500 text-xl mt-0.5">bluetooth_disabled</span>
                                <div className="flex-1">
                                    <div className="text-red-400 font-bold text-sm mb-1">{t('bluetooth.not_available', '蓝牙功能不可用')}</div>
                                    <div className="text-red-400/70 text-xs leading-relaxed">{reason}</div>
                                </div>
                            </div>
                            {isAndroidWebView && (
                                <div className="px-4 pb-4 space-y-2">
                                    {/* Primary: Native Bridge hint for App developers */}
                                    <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-400/80 leading-relaxed">
                                        <div className="font-bold text-amber-400 mb-1 flex items-center gap-1">
                                            <span className="material-icons text-sm">code</span>
                                            {t('bluetooth.dev_hint', '开发者提示（Android App 集成）')}
                                        </div>
                                        {t('bluetooth.dev_hint_desc', '在 WebView 中调用 webView.addJavascriptInterface(AndroidBluetoothBridge(), "AndroidBluetooth") 即可启用原生蓝牙支持。')}
                                    </div>
                                    {/* Fallback: Open in Chrome */}
                                    <a
                                        href={chromeUrl}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-gray-400 font-medium text-sm transition-all active:scale-95"
                                    >
                                        <span className="material-icons text-base">open_in_new</span>
                                        {t('bluetooth.open_chrome', '或用 Chrome 浏览器打开')}
                                    </a>
                                </div>
                            )}
                            {!isAndroidWebView && !window.isSecureContext && (
                                <div className="px-4 pb-4 text-[11px] text-amber-500/70 flex items-start gap-1.5">
                                    <span className="material-icons text-xs mt-0.5">info</span>
                                    <span>{t('bluetooth.secure_context_required', '当前页面以 HTTP 方式加载。请联系管理员启用 HTTPS。')}</span>
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* ─── Section: Bluetooth Scale ─── */}
                <section className="animate-fade-in" style={{ animationDelay: '0.1s' }}>
                    <h2 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em] mb-3 ml-2 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_rgba(19,91,236,0.6)]" />
                        {t('settings.bluetooth_scale', '蓝牙电子秤')}
                    </h2>

                    <div className="bg-surface-dark/40 backdrop-blur-md rounded-[2rem] border border-white/5 overflow-hidden shadow-2xl relative">
                        {/* Decorative background element */}
                        <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-[80px]" />

                        {scaleDevice ? (
                            <>
                                {/* Connected device info */}
                                <div className="p-6 relative z-10">
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="flex items-center gap-4">
                                            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-primary to-blue-600 text-white shadow-xl shadow-primary/20">
                                                <span className="material-icons text-2xl">monitor_weight</span>
                                            </div>
                                            <div>
                                                <div className="text-white font-black text-lg leading-tight">{scaleDevice.name}</div>
                                                <div className="text-slate-500 text-[10px] font-mono mt-1 flex items-center gap-1.5">
                                                    <span className="material-icons text-[10px]">fingerprint</span>
                                                    {scaleDevice.id.slice(0, 16).toUpperCase()}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                <span className="text-emerald-500 text-[10px] font-black uppercase tracking-wider">{t('bluetooth.online', 'Online')}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Premium Weight Display */}
                                    <div className="bg-slate-900/60 rounded-[1.5rem] p-8 text-center border border-white/5 relative overflow-hidden group">
                                        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                                        <div className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] mb-4">{t('bluetooth.live_reading', 'Live Reading')}</div>

                                        <div className="relative inline-block">
                                            <div className={`text-7xl font-black text-white tabular-nums tracking-tighter transition-all duration-300 ${weightPulse ? 'scale-110 text-primary drop-shadow-[0_0_20px_rgba(19,91,236,0.4)]' : ''}`}>
                                                {lastWeight ? lastWeight.weight.toFixed(2) : '0.00'}
                                            </div>
                                            <span className="absolute -right-10 bottom-2 text-xl font-black text-slate-600 uppercase italic">
                                                {lastWeight?.unit || 'kg'}
                                            </span>
                                        </div>

                                        <div className="mt-6 flex justify-center">
                                            <div className={`flex items-center gap-2 px-4 py-1.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${lastWeight?.stable ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500 animate-pulse'}`}>
                                                <span className="material-icons text-sm">{lastWeight?.stable ? 'verified' : 'sensors'}</span>
                                                {lastWeight?.stable ? t('bluetooth.stable_data', 'Stable Data') : t('bluetooth.stabilizing', 'Stabilizing...')}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Disconnect action */}
                                <div className="px-6 pb-6">
                                    <button
                                        onClick={() => handleDisconnect('scale')}
                                        className="w-full py-4 rounded-2xl bg-white/5 text-slate-400 text-xs font-black uppercase tracking-[0.2em] border border-white/5 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20 active:scale-95 transition-all outline-none"
                                    >
                                        {t('bluetooth.disconnect_scale', 'Disconnect Scale')}
                                    </button>
                                </div>
                            </>
                        ) : (
                            /* Not connected state */
                            <div className="p-10 text-center relative z-10">
                                <div className="w-20 h-20 rounded-3xl bg-white/5 flex items-center justify-center mx-auto mb-6 border border-white/5 shadow-inner group transition-all hover:border-primary/30">
                                    <span className="material-icons text-5xl text-slate-700 group-hover:text-primary/40 transition-colors">monitor_weight</span>
                                </div>
                                <h3 className="text-white font-black text-lg mb-2">{t('bluetooth.no_scale', 'No Scale Connected')}</h3>
                                <p className="text-slate-500 text-xs mb-8 max-w-[200px] mx-auto leading-relaxed">{t('bluetooth.no_scale_desc', '连接蓝牙电子秤，实现高精度实时称重。')}</p>
                                <button
                                    onClick={() => openBleModal('scale')}
                                    disabled={!btSupported}
                                    className="w-full flex items-center justify-center gap-3 py-4 bg-primary text-white font-black text-sm rounded-2xl shadow-xl shadow-primary/20 active:scale-95 transition-all disabled:opacity-20 disabled:grayscale"
                                >
                                    <span className="material-icons text-xl">bluetooth_searching</span>
                                    <span>{t('bluetooth.scan_devices', '扫描设备')}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </section>

                {/* ─── Section: Bluetooth Printer ─── */}
                <section className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
                    <h2 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em] mb-3 ml-2 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.6)]" />
                        {t('settings.bluetooth_printer', '蓝牙标签打印机')}
                    </h2>

                    <div className="bg-surface-dark/40 backdrop-blur-md rounded-[2rem] border border-white/5 overflow-hidden shadow-2xl relative">
                        {/* Decorative background element */}
                        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-orange-500/10 rounded-full blur-[80px]" />

                        {printerDevice ? (
                            <>
                                {/* Connected printer info */}
                                <div className="p-6 relative z-10">
                                    <div className="flex items-center justify-between mb-8">
                                        <div className="flex items-center gap-4">
                                            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-xl shadow-orange-500/20">
                                                <span className="material-icons text-2xl">print</span>
                                            </div>
                                            <div>
                                                <div className="text-white font-black text-lg leading-tight">{printerDevice.name}</div>
                                                <div className="text-slate-500 text-[10px] font-mono mt-1 flex items-center gap-1.5">
                                                    <span className="material-icons text-[10px]">qr_code_2</span>
                                                    {printerDevice.id.slice(0, 16).toUpperCase()}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                            <span className="text-emerald-500 text-[10px] font-black uppercase tracking-wider">{t('bluetooth.ready', 'Ready')}</span>
                                        </div>
                                    </div>

                                    {/* Optimized Selectors */}
                                    <div className="space-y-6">
                                        <div>
                                            <div className="text-slate-500 text-[9px] font-black uppercase tracking-[0.3em] mb-4 ml-1">{t('bluetooth.media_geometry', 'Media Geometry')}</div>
                                            <div className="grid grid-cols-3 gap-3">
                                                {PRINT_SIZES.map((s) => (
                                                    <button
                                                        key={s.value}
                                                        onClick={() => setPrintSize(s.value)}
                                                        className={`flex flex-col items-center justify-center py-4 px-2 rounded-2xl border-2 transition-all active:scale-95 group relative overflow-hidden ${printSize === s.value
                                                            ? 'border-primary bg-primary/10 shadow-lg shadow-primary/5'
                                                            : 'border-white/5 bg-slate-900/40 hover:bg-slate-800/60'
                                                            }`}
                                                    >
                                                        {printSize === s.value && <div className="absolute top-0 right-0 w-6 h-6 bg-primary rounded-bl-xl flex items-center justify-center"><span className="material-icons text-white text-[12px]">check</span></div>}
                                                        <span className={`material-icons text-2xl mb-2 transition-transform group-hover:scale-110 ${printSize === s.value ? 'text-primary' : 'text-slate-600'
                                                            }`}>{s.icon}</span>
                                                        <span className={`text-[11px] font-black tracking-tight ${printSize === s.value ? 'text-white' : 'text-slate-400'
                                                            }`}>{s.label}</span>
                                                        <span className={`text-[8px] font-bold uppercase tracking-widest mt-0.5 ${printSize === s.value ? 'text-primary/60' : 'text-slate-600'
                                                            }`}>mm</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="text-slate-500 text-[9px] font-black uppercase tracking-[0.3em] mb-4 ml-1">{t('bluetooth.command_set', 'Command Set (RAW)')}</div>
                                            <div className="grid grid-cols-1 gap-2.5">
                                                {protocols.map((p) => (
                                                    <button
                                                        key={p.value}
                                                        onClick={() => setPrintProtocol(p.value)}
                                                        className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-all active:scale-[0.98] ${printProtocol === p.value
                                                            ? 'border-orange-500/40 bg-orange-500/5'
                                                            : 'border-white/5 bg-slate-900/40 hover:bg-slate-800/60'
                                                            }`}
                                                    >
                                                        <div className="flex items-center gap-4">
                                                            <div className={`p-2.5 rounded-xl transition-colors ${printProtocol === p.value ? 'bg-orange-500 text-white' : 'bg-white/5 text-slate-600'}`}>
                                                                <span className="material-icons text-lg">{p.value === 'auto' ? 'psychology' : 'code'}</span>
                                                            </div>
                                                            <div className="text-left">
                                                                <span className={`text-sm font-black block leading-none mb-1 ${printProtocol === p.value ? 'text-white' : 'text-slate-400'}`}>{p.label}</span>
                                                                <span className="text-[10px] text-slate-600 font-medium">{p.desc}</span>
                                                            </div>
                                                        </div>
                                                        {printProtocol === p.value && (
                                                            <span className="material-icons text-orange-500 text-xl">check_circle</span>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="p-6 flex gap-3 relative z-10">
                                    <button
                                        disabled={testPrinting}
                                        onClick={async () => {
                                            setTestPrinting(true);
                                            try {
                                                // @ts-ignore
                                                await printTest(printProtocol, printSize as any);
                                                toast.success(t('bluetooth.test_label_issued', 'Test label issued'));
                                            } catch (err: any) {
                                                toast.error(err.message || t('bluetooth.print_failed', 'Print failed'));
                                            } finally {
                                                setTestPrinting(false);
                                            }
                                        }}
                                        className="flex-[1.5] py-4 rounded-2xl bg-orange-500 text-white text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-orange-500/20 active:scale-95 transition-all flex items-center justify-center gap-2 hover:bg-orange-600 outline-none disabled:opacity-50"
                                    >
                                        {testPrinting ? (
                                            <span className="material-icons text-lg animate-spin">refresh</span>
                                        ) : (
                                            <span className="material-icons text-lg">receipt_long</span>
                                        )}
                                        {testPrinting ? t('bluetooth.printing', 'Printing...') : t('bluetooth.test_print', 'Test Print')}
                                    </button>
                                    <button
                                        onClick={() => handleDisconnect('printer')}
                                        className="flex-1 py-4 rounded-2xl bg-white/5 text-slate-400 text-xs font-black uppercase tracking-[0.2em] border border-white/5 hover:bg-red-500/10 hover:text-red-400 active:scale-95 transition-all outline-none"
                                    >
                                        {t('bluetooth.drop_connection', 'Drop')}
                                    </button>
                                </div>
                            </>
                        ) : (
                            /* Not connected state */
                            <div className="p-10 text-center relative z-10">
                                <div className="w-20 h-20 rounded-3xl bg-white/5 flex items-center justify-center mx-auto mb-6 border border-white/5 shadow-inner group transition-all hover:border-orange-500/30">
                                    <span className="material-icons text-5xl text-slate-700 group-hover:text-orange-500/40 transition-colors">print</span>
                                </div>
                                <h3 className="text-white font-black text-lg mb-2">{t('bluetooth.printer_offline', 'Printer Offline')}</h3>
                                <p className="text-slate-500 text-xs mb-8 max-w-[200px] mx-auto leading-relaxed">{t('bluetooth.printer_desc', '连接标签打印机，实现面单高速自动打印。')}</p>
                                <button
                                    onClick={() => openBleModal('printer')}
                                    disabled={!btSupported}
                                    className="w-full flex items-center justify-center gap-3 py-4 bg-orange-500 text-white font-black text-sm rounded-2xl shadow-xl shadow-orange-500/20 active:scale-95 transition-all disabled:opacity-20 disabled:grayscale"
                                >
                                    <span className="material-icons text-xl">bluetooth_searching</span>
                                    <span>{t('bluetooth.scan_printers', '扫描打印机')}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </section>
            </main>

            {/* Premium Sticky Bottom Bar */}
            <div className="bg-slate-950/80 backdrop-blur-2xl border-t border-white/5 pb-10 pt-4 z-50 fixed bottom-0 w-full px-6 flex justify-center">
                <div className="max-w-md w-full flex gap-3 animate-slide-up">
                    {!scaleDevice && (
                        <button
                            onClick={() => openBleModal('scale')}
                            disabled={!btSupported}
                            className="flex-1 py-4.5 rounded-2xl bg-gradient-to-r from-primary to-blue-600 text-white text-[11px] font-black uppercase tracking-[0.2em] shadow-[0_10px_30px_rgba(19,91,236,0.3)] active:scale-95 transition-all disabled:opacity-10 flex items-center justify-center gap-2"
                        >
                            <span className="material-icons text-lg">monitor_weight</span>
                            {t('bluetooth.link_scale', 'Link Scale')}
                        </button>
                    )}
                    {!printerDevice && (
                        <button
                            onClick={() => openBleModal('printer')}
                            disabled={!btSupported}
                            className="flex-1 py-4.5 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[11px] font-black uppercase tracking-[0.2em] shadow-[0_10px_30px_rgba(249,115,22,0.3)] active:scale-95 transition-all disabled:opacity-10 flex items-center justify-center gap-2"
                        >
                            <span className="material-icons text-lg">print</span>
                            {t('bluetooth.link_printer', 'Link Printer')}
                        </button>
                    )}
                    {scaleDevice && printerDevice && (
                        <button
                            onClick={() => navigate(-1)}
                            className="flex-1 py-4.5 rounded-[1.5rem] bg-emerald-500/10 text-emerald-500 text-[11px] font-black uppercase tracking-[0.2em] border border-emerald-500/20 active:scale-95 transition-all flex items-center justify-center gap-2 shadow-[0_10px_30px_rgba(16,185,129,0.1)]"
                        >
                            <span className="material-icons text-xl">check_circle</span>
                            {t('bluetooth.system_optimized', 'System Optimized')}
                        </button>
                    )}
                </div>
            </div>

            {/* Unified BLE Connect Modal */}
            {bleModal.show && <BleConnectModal />}
        </div>
    );
};

export default BluetoothConnection;
